import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Steps,
  Tag,
  message,
} from 'antd';
import { ArrowLeftOutlined, CheckCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import purchaseDealSheetService from '../../../../services/purchaseDealSheetService';
import tradeCommitmentService from '../../../../services/tradeCommitmentService';
import AsyncEntitySelect from './AsyncEntitySelect';
import FinancialSummaryCard from './FinancialSummaryCard';
import {
  CREATE_STEPS,
  MODE_OF_DELIVERY_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PRODUCT_SEARCH_MIN,
  SUPPLIER_SEARCH_MIN,
  TRANSACTION_TYPE_OPTIONS,
} from './purchaseDealSheet.constants';
import {
  apiErrMsg,
  buildCreatePayload,
  formatCurrency,
  resolveDealSheetId,
  toNumber,
  validatePartialPaidAmount,
} from './purchaseDealSheet.helpers';

const STEP_FIELDS = [
  ['product_id', 'isin'],
  ['transaction_date', 'mode_of_delivery', 'quantity', 'price_per_share'],
  ['payment_status', 'paid_amount'],
];

const FULL_WIDTH = { width: '100%' };

const FORM_FIELD_GRID_CLASS =
  'grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-0 [&_.ant-form-item]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item-control-input]:w-full [&_.ant-form-item-control-input-content]:w-full';

const SUPPLIER_PREVIEW_FIELDS = [
  { label: 'PAN', key: 'seller_pan' },
  { label: 'Bank', key: 'seller_bank_name' },
  { label: 'A/C Number', key: 'seller_account_number' },
  { label: 'IFSC', key: 'seller_ifsc_code' },
  { label: 'DP Name', key: 'seller_dp_name' },
  { label: 'DP ID', key: 'seller_dp_id' },
  { label: 'Client ID', key: 'seller_client_id' },
];

function SectionLabel({ children }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-3 m-0">
      {children}
    </p>
  );
}

export default function PurchaseDealSheetCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromCommitmentId = searchParams.get('from_commitment');
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [sourceCommitmentId, setSourceCommitmentId] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [isinOptions, setIsinOptions] = useState([]);

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierSnapshot, setSupplierSnapshot] = useState(null);
  const [supplierLoading, setSupplierLoading] = useState(false);

  const [amounts, setAmounts] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const calculateTimerRef = useRef(null);

  const paymentStatus = Form.useWatch('payment_status', form);

  useEffect(() => {
    form.setFieldsValue({
      transaction_type: 'PURCHASE_OF_EQUITY_SHARES',
      mode_of_delivery: 'DEMAT',
      payment_status: 'UNPAID',
      stamp_duty_percent: 0.015,
      tds_percent: 0,
      transaction_date: dayjs(),
    });
  }, [form]);

  const fetchProducts = useCallback(
    (search) => purchaseDealSheetService.listProducts({ search, page: 1, limit: 50 }),
    [],
  );

  const fetchSuppliers = useCallback(
    (search) => purchaseDealSheetService.listSuppliers({ search, page: 1, limit: 20 }),
    [],
  );

  const handleProductSelect = useCallback(
    async (item) => {
      setSelectedProduct(item);
      setProductDetail(null);
      setIsinOptions([]);
      form.setFieldValue('isin', undefined);
      if (!item) return;
      try {
        const detail = await purchaseDealSheetService.getProductById(item.uuid ?? item.id);
        setProductDetail(detail);
        const isins = Array.isArray(detail?.isin_numbers) ? detail.isin_numbers : [];
        setIsinOptions(isins.map((v) => ({ value: v, label: v })));
        if (isins.length === 1) form.setFieldValue('isin', isins[0]);
      } catch (e) {
        message.error(apiErrMsg(e));
      }
    },
    [form],
  );

  const handleSupplierSelect = useCallback(async (item) => {
    setSelectedSupplier(item);
    setSupplierSnapshot(null);
    if (!item) return;
    setSupplierLoading(true);
    try {
      const snapshot = await purchaseDealSheetService.getSupplierById(item.uuid ?? item.id);
      setSupplierSnapshot(snapshot);
    } catch (e) {
      message.error(apiErrMsg(e));
    } finally {
      setSupplierLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fromCommitmentId) return undefined;

    let cancelled = false;

    const prefillFromCommitment = async () => {
      setPrefillLoading(true);
      try {
        const commitment = await tradeCommitmentService.get(fromCommitmentId);
        if (cancelled || commitment.direction !== 'BUY' || commitment.status !== 'ACTIVE') return;

        setSourceCommitmentId(fromCommitmentId);
        form.setFieldsValue({
          quantity: commitment.quantity,
          price_per_share: commitment.price_per_share,
        });

        const productItem = {
          id: commitment.product_numeric_id,
          uuid: commitment.product_id,
          name: commitment.product_name,
        };
        form.setFieldValue('product_id', commitment.product_name);
        await handleProductSelect(productItem);

        if (commitment.counterparty_id) {
          const supplierItem = {
            uuid: commitment.counterparty_id,
            id: commitment.counterparty_id,
            name: commitment.counterparty_name,
          };
          form.setFieldValue('supplier_id', commitment.counterparty_name);
          await handleSupplierSelect(supplierItem);
        } else if (commitment.counterparty_name) {
          const suppliers = await purchaseDealSheetService.listSuppliers({
            search: commitment.counterparty_name,
            limit: 20,
          });
          const items = suppliers.items || [];
          if (items.length === 1) {
            form.setFieldValue('supplier_id', items[0].name);
            await handleSupplierSelect(items[0]);
          } else {
            form.setFieldValue('supplier_id', commitment.counterparty_name);
          }
        }
      } catch {
        if (!cancelled) message.warning('Could not prefill from commitment');
      } finally {
        if (!cancelled) setPrefillLoading(false);
      }
    };

    prefillFromCommitment();
    return () => {
      cancelled = true;
    };
  }, [fromCommitmentId, form, handleProductSelect, handleSupplierSelect]);

  const runCalculate = useCallback(async () => {
    const qty = form.getFieldValue('quantity');
    const price = form.getFieldValue('price_per_share');
    if (!qty || !price) { setAmounts(null); return; }
    setCalculating(true);
    try {
      const result = await purchaseDealSheetService.calculateAmounts({
        quantity: toNumber(qty),
        price_per_share: toNumber(price),
        stamp_duty_percent: toNumber(form.getFieldValue('stamp_duty_percent') ?? 0),
        tds_percent: toNumber(form.getFieldValue('tds_percent') ?? 0),
      });
      setAmounts(result);
    } catch (e) {
      setAmounts(null);
      message.error(apiErrMsg(e));
    } finally {
      setCalculating(false);
    }
  }, [form]);

  const scheduleCalculate = useCallback(() => {
    clearTimeout(calculateTimerRef.current);
    calculateTimerRef.current = setTimeout(runCalculate, 400);
  }, [runCalculate]);

  useEffect(() => () => clearTimeout(calculateTimerRef.current), []);

  const validateStep = async (step) => {
    await form.validateFields(STEP_FIELDS[step] || []);
    if (step === 0) {
      if (!selectedProduct) throw new Error('Select a product');
      if (isinOptions.length > 1 && !form.getFieldValue('isin')) throw new Error('Select an ISIN');
      if (!selectedSupplier) throw new Error('Select a supplier');
    }
    if (step === 1) {
      await runCalculate();
      if (!amounts || toNumber(amounts.total_payable) <= 0)
        throw new Error('Total payable must be greater than zero');
    }
    if (step === 2 && paymentStatus === 'PARTIAL') {
      const err = validatePartialPaidAmount(form.getFieldValue('paid_amount'), amounts?.total_payable);
      if (err) throw new Error(err);
    }
  };

  const handleNext = async () => {
    try {
      await validateStep(currentStep);
      setCurrentStep((s) => Math.min(s + 1, CREATE_STEPS.length - 1));
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Please complete required fields');
    }
  };

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    try {
      await validateStep(2);
      const values = form.getFieldsValue(true);
      const payload = buildCreatePayload(values, {
        supplierId: selectedSupplier?.uuid ?? selectedSupplier?.id,
        productId: selectedProduct?.uuid ?? selectedProduct?.id,
        isin: values.isin,
        tradeCommitmentId: sourceCommitmentId,
      });
      setSubmitting(true);
      const created = await purchaseDealSheetService.createDealSheet(payload);
      message.success('Purchase deal sheet created');
      navigate(`/pre-ipo/purchase-deal-sheet/${encodeURIComponent(String(resolveDealSheetId(created)))}`);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(apiErrMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step 1: Deal ──────────────────────────────────────────────────────────

  const renderDeal = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

      {/* Product column */}
      <div className="flex flex-col gap-1">
        <SectionLabel>Product</SectionLabel>
        <Form.Item
          name="product_id"
          className="mb-0"
          rules={[{ required: true, message: 'Select a product' }]}
        >
          <AsyncEntitySelect
            minSearchLength={PRODUCT_SEARCH_MIN}
            placeholder="Search by company name or ISIN"
            searchHint="Type to search products"
            fetchOptions={fetchProducts}
            onSelectItem={handleProductSelect}
          />
        </Form.Item>

        {/* ISIN — multi select */}
        {isinOptions.length > 1 && (
          <Form.Item
            name="isin"
            label="Select ISIN"
            className="mt-3 mb-0"
            rules={[{ required: true, message: 'Select an ISIN' }]}
          >
            <Select className="w-full" style={FULL_WIDTH} options={isinOptions} placeholder="Select ISIN" />
          </Form.Item>
        )}
        {/* ISIN hidden when single */}
        <Form.Item name="isin" hidden><Input /></Form.Item>

        {/* ISIN auto-set badge */}
        {productDetail && isinOptions.length === 1 && (
          <div className="flex items-center gap-1.5 mt-2">
            <CheckCircleFilled className="text-green-500 text-xs" />
            <span className="text-xs text-gray-500">ISIN auto-selected:</span>
            <Tag className="m-0 font-mono text-xs">{isinOptions[0].value}</Tag>
          </div>
        )}
      </div>

      {/* Supplier column */}
      <div className="flex flex-col gap-1">
        <SectionLabel>Supplier</SectionLabel>
        <Form.Item
          name="supplier_id"
          className="mb-0"
          rules={[{ required: true, message: 'Select a supplier' }]}
        >
          <AsyncEntitySelect
            minSearchLength={SUPPLIER_SEARCH_MIN}
            placeholder="Search by name or PAN"
            searchHint="Type at least 3 characters to search"
            fetchOptions={fetchSuppliers}
            onSelectItem={handleSupplierSelect}
          />
        </Form.Item>

        {/* Supplier snapshot */}
        {supplierLoading && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
            <Spin size="small" /><span>Loading supplier details…</span>
          </div>
        )}
        {supplierSnapshot && !supplierLoading && (
          <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {SUPPLIER_PREVIEW_FIELDS.map(({ label, key }) => (
              <div key={key} className="min-w-0">
                <p className="text-[11px] text-gray-400 font-medium mb-0.5">{label}</p>
                <p
                  className="text-sm text-gray-800 font-medium m-0 truncate"
                  title={supplierSnapshot[key] ?? '—'}
                >
                  {supplierSnapshot[key] ?? '—'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ─── Step 2: Details ────────────────────────────────────────────────────────

  const renderDetails = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

      {/* Left fields — 2/3 width */}
      <div className="lg:col-span-2 flex flex-col gap-0">
        <SectionLabel>Transaction</SectionLabel>
        <div className={FORM_FIELD_GRID_CLASS}>
          <Form.Item name="transaction_type" label="Transaction Type" rules={[{ required: true }]}>
            <Select className="w-full" style={FULL_WIDTH} options={TRANSACTION_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="mode_of_delivery"
            label="Mode of Delivery"
            rules={[{ required: true, message: 'Select delivery mode' }]}
          >
            <Select className="w-full" style={FULL_WIDTH} options={MODE_OF_DELIVERY_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="transaction_date"
            label="Transaction Date"
            rules={[{ required: true, message: 'Select transaction date' }]}
          >
            <DatePicker className="w-full" style={FULL_WIDTH} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="payment_date" label="Payment Date">
            <DatePicker className="w-full" style={FULL_WIDTH} format="DD MMM YYYY" />
          </Form.Item>
        </div>

        <div className="border-t border-gray-100 my-2" />
        <SectionLabel>Financials</SectionLabel>

        <div className={FORM_FIELD_GRID_CLASS}>
          <Form.Item
            name="quantity"
            label="Quantity"
            rules={[
              { required: true, message: 'Enter quantity' },
              { type: 'number', min: 0.0001, message: 'Must be positive' },
            ]}
          >
            <InputNumber className="w-full" style={FULL_WIDTH} min={0.0001} onChange={scheduleCalculate} />
          </Form.Item>
          <Form.Item
            name="price_per_share"
            label="Price per Share"
            rules={[
              { required: true, message: 'Enter price per share' },
              { type: 'number', min: 0.0001, message: 'Must be positive' },
            ]}
          >
            <InputNumber className="w-full" style={FULL_WIDTH} min={0.0001} precision={2} onChange={scheduleCalculate} />
          </Form.Item>
          <Form.Item name="stamp_duty_percent" label="Stamp Duty (%)">
            <InputNumber className="w-full" style={FULL_WIDTH} min={0} step={0.001} onChange={scheduleCalculate} />
          </Form.Item>
          <Form.Item name="tds_percent" label="TDS (%)">
            <InputNumber className="w-full" style={FULL_WIDTH} min={0} step={0.001} onChange={scheduleCalculate} />
          </Form.Item>
        </div>
      </div>

      {/* Right summary — 1/3 width */}
      <div className="lg:col-span-1">
        <SectionLabel>Live Summary</SectionLabel>
        <FinancialSummaryCard amounts={amounts} loading={calculating} />
      </div>
    </div>
  );

  // ─── Step 3: Payment ────────────────────────────────────────────────────────

  const renderPayment = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

      {/* Left payment form — 2/3 width */}
      <div className="lg:col-span-2 flex flex-col gap-0">
        <SectionLabel>Payment Details</SectionLabel>

        <Form.Item name="payment_status" label="Payment Status" rules={[{ required: true }]}>
          <Select className="w-full" style={FULL_WIDTH} options={PAYMENT_STATUS_OPTIONS} />
        </Form.Item>

        {paymentStatus === 'PARTIAL' && (
          <div className={FORM_FIELD_GRID_CLASS}>
            <Form.Item
              name="paid_amount"
              label="Paid Amount"
              rules={[
                { required: true, message: 'Enter paid amount' },
                {
                  validator: (_, value) => {
                    const err = validatePartialPaidAmount(value, amounts?.total_payable);
                    return err ? Promise.reject(new Error(err)) : Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber className="w-full" style={FULL_WIDTH} min={0.01} precision={2} placeholder="0.00" />
            </Form.Item>
            <Form.Item name="payment_date" label="Payment Date">
              <DatePicker className="w-full" style={FULL_WIDTH} format="DD MMM YYYY" />
            </Form.Item>
          </div>
        )}

        {paymentStatus === 'PAID' && (
          <Form.Item name="payment_date" label="Payment Date">
            <DatePicker className="w-full" style={FULL_WIDTH} format="DD MMM YYYY" />
          </Form.Item>
        )}

        {paymentStatus !== 'UNPAID' && (
          <>
            <Form.Item name="utr_number" label="UTR Number">
              <Input className="w-full" maxLength={128} placeholder="Enter UTR number" />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea rows={3} maxLength={2000} showCount placeholder="Any remarks" />
            </Form.Item>
          </>
        )}

        <div className="flex items-start gap-2 mt-1 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2.5 text-xs text-blue-700">
          <span className="mt-0.5 text-blue-400">ℹ</span>
          <span>Default buyer snapshot (Alt-Alpha) will be applied by the server automatically.</span>
        </div>
      </div>

      {/* Right amount card — 1/3 width */}
      <div className="lg:col-span-1">
        <SectionLabel>Amount to Pay</SectionLabel>
        {amounts ? (
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 flex flex-col gap-3">
            {[
              { label: 'Consideration', value: amounts.consideration ?? amounts.total_consideration },
              { label: 'Stamp Duty', value: amounts.stamp_duty_amount },
              { label: 'TDS', value: amounts.tds_amount },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{label}</span>
                <span className="text-gray-800">{formatCurrency(value)}</span>
              </div>
            ))}
            <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Total Payable</span>
              <span className="text-base font-bold text-gray-900">
                {formatCurrency(amounts.total_payable ?? amounts.total_amount)}
              </span>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-400 italic">
            Complete Step 2 to see calculated amounts.
          </div>
        )}
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderDeal();
      case 1: return renderDetails();
      case 2: return renderPayment();
      default: return null;
    }
  };

  return (
    <DashboardPageWrapper>
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 pt-1 pb-5 flex flex-col gap-4">

        {/* Page header + steps */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/pre-ipo/purchase-deal-sheet')}
              className="-ml-1 shrink-0 px-1.5 text-gray-500 hover:text-gray-900"
            >
              Back
            </Button>
            <div className="min-w-0 border-l border-gray-200 pl-2.5">
              {/* <h1 className="text-base sm:text-lg font-bold text-gray-900 m-0 leading-snug truncate">
                Create Deal Sheet
              </h1> */}
              <p className="text-[11px] sm:text-xs text-gray-400 mt-0 mb-0 leading-tight">
                Create new Pre-IPO purchase deal sheet
              </p>
            </div>
          </div>

          <Steps
            current={currentStep}
            items={CREATE_STEPS}
            size="small"
            className="max-w-sm [&_.ant-steps-item]:pb-0 [&_.ant-steps-item-container]:flex [&_.ant-steps-item-title]:leading-tight"
          />
        </div>

        {sourceCommitmentId && (
          <Alert
            type="info"
            showIcon
            message="Prefilled from trade commitment"
            description="Review product, supplier, ISIN, and stamp duty before saving. The commitment will be linked automatically."
          />
        )}

        {prefillLoading && (
          <div className="flex justify-center py-4">
            <Spin tip="Loading commitment…" />
          </div>
        )}

        {/* Form card */}
          <Card
            className="shadow-sm"
            classNames={{ body: 'p-4 sm:p-7 sm:pb-6' }}
          >
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              className="[&_.ant-form-item]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item-control-input]:w-full [&_.ant-form-item-control-input-content]:w-full"
            >
              {renderStepContent()}

              {/* Navigation footer */}
              <div className="flex items-center justify-end gap-2 pt-6 mt-6 border-t border-gray-100">
                {currentStep > 0 && (
                  <Button onClick={handleBack} disabled={submitting}>
                    Back
                  </Button>
                )}
                {currentStep < CREATE_STEPS.length - 1 ? (
                  <Button type="primary" onClick={handleNext}>
                    Next
                  </Button>
                ) : (
                  <Button type="primary" loading={submitting} onClick={handleSubmit}>
                    Create Deal Sheet
                  </Button>
                )}
              </div>
            </Form>
          </Card>
      </div>
    </DashboardPageWrapper>
  );
}
